import React, { useMemo } from 'react';
import { Layer, Line, Circle, Text, Rect, Group } from 'react-konva';
import { CELL_SIZE } from './GridLayer';
import { WALL_STYLES } from './WallLayer';
import { useMap } from '../../contexts/MapContext';
import { useAuth } from '../../contexts/AuthContext';
import { segmentMidpoint, segmentAngle } from '../../lib/geometry';

// Door-specific visual styles
const DOOR_STYLES = {
  wood:  { stroke: '#d4914a', frame: '#8a5a30', knob: '#f0c060' },
  iron:  { stroke: '#6a7a8a', frame: '#4a5a6a', knob: '#9aa0a8' },
  stone: { stroke: '#8a8a9a', frame: '#5a5a6a', knob: '#b0b0c0' },
};

const BARS_STROKE = '#5a6070';

function px(v) { return v * CELL_SIZE; }

/**
 * Renders a single door segment with frame ticks, knob, and HP bar.
 */
function DoorElement({ door, isDm, selected, onClick, walls, viewerPlayerId }) {
  const [a, b] = door.pts;
  const mid    = segmentMidpoint(a, b);
  const angle  = segmentAngle(a, b);
  const len    = Math.hypot(b.x - a.x, b.y - a.y);

  // Perpendicular unit vector (rotated 90 degrees)
  const nx = -Math.sin(angle);
  const ny =  Math.cos(angle);

  const isSecret    = door.type === 'secret';
  const isBars      = door.type === 'bars';
  const isOpen      = door.state === 'open';
  const isLocked    = door.state === 'locked';
  const showDamage  = door.hp < door.maxHp;

  // Secret doors: players see a plain wall unless they've personally discovered
  // it via perception (door.discoveredBy[viewerPlayerId]). DM always sees the
  // door + the purple "S" marker.
  const viewerDiscovered = viewerPlayerId && door.discoveredBy?.[viewerPlayerId];
  if (isSecret && !door.secretRevealed && !viewerDiscovered) {
    const connectedStyle = findConnectedWallStyle(door, walls);
    const wStyle = WALL_STYLES[connectedStyle] || WALL_STYLES.stone;
    return (
      <Group>
        {/* Render as a seamless wall segment — butt caps, no shadow */}
        <Line
          points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
          stroke={wStyle.stroke}
          strokeWidth={wStyle.strokeWidth}
          lineCap="butt"
          lineJoin="miter"
          listening={false}
        />
        {/* DM-only: tiny "S" marker at midpoint */}
        {isDm && <>
          <Text
            x={px(mid.x) - 4} y={px(mid.y) - 5}
            text="S" fontSize={9} fontStyle="bold"
            fill="#c084fc" opacity={0.7}
            listening={false}
            shadowColor="#000" shadowBlur={2}
          />
          {/* Invisible hit area for DM clicking */}
          <Line
            points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
            stroke="transparent"
            strokeWidth={24}
            hitStrokeWidth={28}
            lineCap="round"
            onClick={() => onClick?.(door.id)}
            listening={!!onClick}
          />
          {selected && (
            <Line
              points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
              stroke="#f5c842"
              strokeWidth={14}
              lineCap="round"
              opacity={0.4}
              listening={false}
            />
          )}
        </>}
      </Group>
    );
  }

  const dStyle = DOOR_STYLES[door.style] || DOOR_STYLES.wood;
  const tickLen = 0.3; // frame tick length in grid units

  // Frame tick endpoints
  const ticks = [a, b].flatMap((p) => [
    [px(p.x + nx * tickLen), px(p.y + ny * tickLen)],
    [px(p.x - nx * tickLen), px(p.y - ny * tickLen)],
  ]);

  return (
    <Group>
      {/* Invisible hit area for clicking */}
      <Line
        points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
        stroke="transparent"
        strokeWidth={24}
        hitStrokeWidth={28}
        lineCap="round"
        onClick={() => onClick?.(door.id)}
        listening={!!onClick}
      />

      {/* Selection highlight */}
      {selected && (
        <Line
          points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
          stroke="#f5c842"
          strokeWidth={14}
          lineCap="round"
          opacity={0.4}
          listening={false}
        />
      )}

      {isBars ? (
        // ── Bars rendering ──────────────────────────────────────────────
        <Group listening={false}>
          {/* Top and bottom rails */}
          <Line
            points={[px(a.x + nx * 0.15), px(a.y + ny * 0.15), px(b.x + nx * 0.15), px(b.y + ny * 0.15)]}
            stroke={BARS_STROKE} strokeWidth={3} lineCap="round" opacity={isOpen ? 0.3 : 1}
          />
          <Line
            points={[px(a.x - nx * 0.15), px(a.y - ny * 0.15), px(b.x - nx * 0.15), px(b.y - ny * 0.15)]}
            stroke={BARS_STROKE} strokeWidth={3} lineCap="round" opacity={isOpen ? 0.3 : 1}
          />
          {/* Individual bars */}
          {!isOpen && Array.from({ length: Math.max(2, Math.round(len * 4)) }, (_, i) => {
            const t  = (i + 1) / (Math.round(len * 4) + 1);
            const bx = a.x + t * (b.x - a.x);
            const by = a.y + t * (b.y - a.y);
            return (
              <Line
                key={i}
                points={[px(bx + nx * 0.2), px(by + ny * 0.2), px(bx - nx * 0.2), px(by - ny * 0.2)]}
                stroke={BARS_STROKE} strokeWidth={2} lineCap="round"
              />
            );
          })}
        </Group>
      ) : isOpen ? (
        // ── Open door ───────────────────────────────────────────────────
        <Group listening={false}>
          <Line
            points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
            stroke={dStyle.stroke}
            strokeWidth={4}
            lineCap="round"
            opacity={0.3}
            dash={[6, 4]}
          />
          {/* Frame ticks remain visible */}
          <Line points={[ticks[0][0], ticks[0][1], ticks[1][0], ticks[1][1]]} stroke={dStyle.frame} strokeWidth={3} lineCap="round" opacity={0.5} />
          <Line points={[ticks[2][0], ticks[2][1], ticks[3][0], ticks[3][1]]} stroke={dStyle.frame} strokeWidth={3} lineCap="round" opacity={0.5} />
        </Group>
      ) : (
        // ── Closed / Locked door ────────────────────────────────────────
        <Group listening={false}>
          {/* Dark outline for contrast against terrain */}
          <Line
            points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
            stroke="#1a1020"
            strokeWidth={12}
            lineCap="round"
          />
          {/* Main door line */}
          <Line
            points={[px(a.x), px(a.y), px(b.x), px(b.y)]}
            stroke={dStyle.stroke}
            strokeWidth={8}
            lineCap="round"
            shadowColor="rgba(0,0,0,0.5)"
            shadowBlur={4}
            shadowOffsetX={1}
            shadowOffsetY={1}
          />
          {/* Frame ticks at endpoints */}
          <Line points={[ticks[0][0], ticks[0][1], ticks[1][0], ticks[1][1]]} stroke={dStyle.frame} strokeWidth={4} lineCap="round" />
          <Line points={[ticks[2][0], ticks[2][1], ticks[3][0], ticks[3][1]]} stroke={dStyle.frame} strokeWidth={4} lineCap="round" />
          {/* Knob at midpoint */}
          <Circle x={px(mid.x)} y={px(mid.y)} radius={4.5} fill={dStyle.knob} stroke={dStyle.frame} strokeWidth={1} />
        </Group>
      )}

      {/* Lock icon */}
      {isLocked && (
        <Text
          x={px(mid.x) - 8} y={px(mid.y) - 16}
          text="🔒" fontSize={12}
          listening={false}
        />
      )}

      {/* Secret door revealed — DM hint */}
      {isSecret && door.secretRevealed && isDm && (
        <Text
          x={px(mid.x) - 6} y={px(mid.y) - 18}
          text="S" fontSize={10} fontStyle="bold"
          fill="#a855f7" opacity={0.6}
          listening={false}
          shadowColor="#000" shadowBlur={2}
        />
      )}

      {/* HP bar when damaged */}
      {showDamage && isDm && (
        <Group listening={false}>
          <Rect
            x={px(mid.x) - 15} y={px(mid.y) + 8}
            width={30} height={4}
            fill="#000" opacity={0.6} cornerRadius={2}
          />
          <Rect
            x={px(mid.x) - 15} y={px(mid.y) + 8}
            width={30 * Math.max(0, door.hp / door.maxHp)} height={4}
            fill={door.hp / door.maxHp > 0.5 ? '#4ade80' : door.hp / door.maxHp > 0.25 ? '#facc15' : '#f87171'}
            cornerRadius={2}
          />
        </Group>
      )}
    </Group>
  );
}

/** Find the wall style of the nearest wall endpoint to a door's endpoints */
function findConnectedWallStyle(door, walls) {
  if (!walls || walls.length === 0) return door.secretWallStyle || 'stone';
  const [a, b] = door.pts;
  let bestStyle = door.secretWallStyle || 'stone';
  let bestDist  = Infinity;
  for (const wall of walls) {
    if (!wall.pts || wall.pts.length < 2) continue;
    for (const wp of wall.pts) {
      const dA = Math.hypot(wp.x - a.x, wp.y - a.y);
      const dB = Math.hypot(wp.x - b.x, wp.y - b.y);
      const d = Math.min(dA, dB);
      if (d < bestDist) { bestDist = d; bestStyle = wall.style || 'stone'; }
    }
  }
  return bestDist < 1.0 ? bestStyle : (door.secretWallStyle || 'stone');
}

export default function DoorSegmentLayer({ isDm, selectedDoorId, onDoorClick, editorMode, draftStart = null, draftMousePos = null, bare = false }) {
  const { doors, walls, deleteDoor } = useMap();
  const { auth } = useAuth();
  const viewerPlayerId = auth?.playerId || null;
  const isEraser = editorMode === 'wall-eraser';

  const elements = useMemo(() => {
    if (!doors || doors.length === 0) return null;
    return doors.map((door) => (
      <DoorElement
        key={door.id}
        door={door}
        isDm={isDm}
        selected={door.id === selectedDoorId}
        onClick={isDm ? (isEraser ? (id) => deleteDoor(id) : onDoorClick) : null}
        walls={walls}
        viewerPlayerId={viewerPlayerId}
      />
    ));
  }, [doors, walls, isDm, selectedDoorId, onDoorClick, isEraser, deleteDoor, viewerPlayerId]);

  const body = (
    <>
      {elements}
      {/* Door pen draft preview */}
      {draftStart && draftMousePos && (
        <>
          <Line
            points={[px(draftStart.x), px(draftStart.y), px(draftMousePos.x), px(draftMousePos.y)]}
            stroke="#b87333"
            strokeWidth={6}
            lineCap="round"
            opacity={0.5}
            dash={[8, 4]}
            listening={false}
          />
          <Circle x={px(draftStart.x)} y={px(draftStart.y)} radius={5} fill="#b87333" opacity={0.8} listening={false} />
          <Circle x={px(draftMousePos.x)} y={px(draftMousePos.y)} radius={4} fill="#ffffff" opacity={0.6} listening={false} />
        </>
      )}
      {draftStart && !draftMousePos && (
        <Circle x={px(draftStart.x)} y={px(draftStart.y)} radius={5} fill="#b87333" opacity={0.8} listening={false} />
      )}
    </>
  );

  if (bare) return body;
  return <Layer listening={isDm}>{body}</Layer>;
}
